// frontend/src/hooks/useProyectos.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getProyectos,
  getProyectosMeta,
  createProyecto as apiCreate,
  updateProyecto as apiUpdate,
  activarProyecto as apiActivar,
  removeProyecto as apiRemove,
} from "../api/proyectos";

/* ========= Tipos alineados con el backend ========= */
export type ProyectoRol = "DEFAULT" | "TRAINING" | "A365" | "EXAM" | "HIGHPERF";
export type ProyectoBillingMode = "FACTURABLE" | "NO_FACTURABLE" | "POR_ALUMNOS" | "ESPECIAL";

export type Proyecto = {
  id: number;
  codigo: string;
  nombre: string;
  descripcion?: string | null;
  activo: boolean;

  rol: ProyectoRol;
  billingMode: ProyectoBillingMode;

  includeInReports: boolean;
  countsInTeachingHours: boolean;
  countsInStudentReports: boolean;
  isExamFlag: boolean;

  reportGroup?: string | null;
  calendarColor?: string | null;

  createdAt: string;
  updatedAt: string;
};

export type ProyectoPayload = Omit<Proyecto, "id" | "createdAt" | "updatedAt">;

/* ========= Filtros de listado ========= */
export type ProyectosFilters = {
  q?: string;
  activos?: boolean;
  rol?: ProyectoRol;
  billingMode?: ProyectoBillingMode;
  includeInReports?: boolean;
  countsInTeachingHours?: boolean;
  countsInStudentReports?: boolean;
  isExamFlag?: boolean;
};

/* ========= LISTADO ========= */
export function useProyectos(filters: ProyectosFilters = {}) {
  const { data, isFetching, refetch } = useQuery<Proyecto[]>({
    queryKey: ["proyectos", filters],
    queryFn: () => getProyectos(filters),
    staleTime: 60_000,
  });

  return {
    rows: data ?? [],
    isFetching,
    refetch,
  };
}

/* ========= META (roles / billingModes) ========= */
export function useProyectosMeta() {
  return useQuery<{
    roles: ProyectoRol[];
    billingModes: ProyectoBillingMode[];
  }>({
    queryKey: ["proyectos-meta"],
    queryFn: getProyectosMeta,
    staleTime: 5 * 60_000,
  });
}

/* ========= MUTATIONS (CRUD) ========= */
export function useProyectoMutations() {
  const qc = useQueryClient();

  const createProyecto = useMutation({
    mutationFn: (p: ProyectoPayload) => apiCreate(p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proyectos"] }),
  });

  const updateProyecto = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ProyectoPayload> }) =>
      apiUpdate(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proyectos"] }),
  });

  const activarProyecto = useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) => apiActivar(id, activo),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proyectos"] }),
  });

  const removeProyecto = useMutation({
    mutationFn: (id: number) => apiRemove(id), // backend hace borrado lógico
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proyectos"] }),
  });

  return { createProyecto, updateProyecto, activarProyecto, removeProyecto };
}
